import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { post } from "../../lib/api";
import {
  DISPLAY_LABEL_MAX,
  HEARTBEAT_MIN_S,
  displayLabelError,
  heartbeatError,
  stationIdError,
} from "../../lib/station";
import type { Depot, ProvisionedStation } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { Field, SelectField } from "../ui/Input";
import { SectionTitle } from "../ui/Text";

/**
 * Registering a charge point.
 *
 * Four fields, two of them optional, because that is the whole of what the server
 * accepts: an id, a site, a driver-facing name, and a heartbeat interval that
 * defaults. Notably absent is a password field — the credential is minted
 * server-side and shown once (see `StationCredential`), so there is nothing here
 * for an operator to choose badly.
 *
 * The name is here rather than only on the station's own page because naming is
 * part of installing: whoever is standing at the charger knows what the place is
 * called, and a station that has to be named in a second visit is a station that
 * goes on sale showing drivers an asset tag. It stays editable afterwards
 * (`StationName`), so this is a head start rather than a commitment.
 *
 * The id cannot be changed afterwards: it is the primary key the station
 * authenticates against and the last path segment of its WebSocket URL. The form
 * says so, because "you can fix it later" is the assumption that turns a typo
 * into a row nobody can use.
 *
 * Creating a depot is not part of this form. It used to be, and it was wrong twice
 * over: a `<form>` nested inside this one, which browsers do not allow, and then a
 * depot form that replaced these fields — taking the charge point id off screen
 * mid-task. Sites live on their own page and this form only picks one.
 */

export interface RegisterStationProps {
  readonly depots: readonly Depot[];
  readonly onProvisioned: (station: ProvisionedStation) => void;
  readonly onCancel: () => void;
}

export function RegisterStation({ depots, onProvisioned, onCancel }: RegisterStationProps) {
  const queryClient = useQueryClient();

  const [id, setId] = useState("");
  const [depotId, setDepotId] = useState(depots[0]?.id ?? "");
  const [heartbeat, setHeartbeat] = useState("");
  const [displayLabel, setDisplayLabel] = useState("");

  const register = useMutation({
    mutationFn: (body: unknown) => post<ProvisionedStation>("/charge-points", body),
    onSuccess: (station) => {
      // The new row belongs in the table behind this form, and the operator will
      // look for it there the moment the credential panel is dismissed.
      void queryClient.invalidateQueries({ queryKey: ["charge-points"] });
      onProvisioned(station);
    },
  });

  const idError = stationIdError(id);
  const heartbeatIssue = heartbeatError(heartbeat);
  const nameIssue = displayLabelError(displayLabel);

  const canSubmit =
    id.trim() !== "" &&
    depotId !== "" &&
    idError === undefined &&
    heartbeatIssue === undefined &&
    nameIssue === undefined &&
    !register.isPending;

  return (
    <section
      // A named region: this panel and the fleet filters above it both hold a
      // "Depot" control, and the name is what tells them apart.
      aria-label="Register a charge point"
      className="flex flex-col gap-4 border-l-2 border-ink bg-paper-raised px-4 py-4"
    >
      <SectionTitle
        action={
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        }
      >
        Register a charge point
      </SectionTitle>

      {register.isError ? <ErrorNotice error={register.error} /> : null}

      {depots.length === 0 ? (
        // Nothing to pick, so the form would be a dead end. Sent to the page that
        // can fix it rather than shown an empty picker.
        <p className="m-0 max-w-prose text-body text-ink-muted">
          There are no depots yet, and a station needs one — its site decides the timezone its
          allowance periods resolve in. Create one under{" "}
          <Link
            to="/depots"
            className="font-semibold text-signal underline decoration-1 underline-offset-4 hover:no-underline"
          >
            Depots
          </Link>
          , then come back here.
        </p>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;
            register.mutate({
              id: id.trim(),
              depotId,
              // Omitted rather than sent as a guess: the server's default is the
              // one number that must not be duplicated in two places.
              ...(heartbeat === "" ? {} : { heartbeatIntervalS: Number(heartbeat) }),
              // Omitted rather than sent as `""`, which the server refuses on
              // purpose: the driver screens fall back to the id on a null name,
              // and an empty string would publish an empty nameplate.
              ...(displayLabel.trim() === "" ? {} : { displayLabel: displayLabel.trim() }),
            });
          }}
          className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2 xl:grid-cols-3"
        >
          <Field
            label="Charge point id"
            value={id}
            onChange={(event) => setId(event.target.value)}
            placeholder="CP-0042"
            hint="Letters, digits, dot, underscore, hyphen — no colon. Must match the id the station is configured with. Permanent — it is the station's own identifier on the wire."
            required
            autoFocus
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            {...(idError === undefined ? {} : { error: idError })}
          />

          <div className="flex flex-col gap-1">
            <SelectField
              label="Depot"
              value={depotId}
              onChange={(event) => setDepotId(event.target.value)}
              required
            >
              {depots.map((depot) => (
                <option key={depot.id} value={depot.id}>
                  {depot.name} · {depot.timezone}
                </option>
              ))}
            </SelectField>
            {/*
              A link, not a button that swaps this form out. Creating a site is a
              decision with its own page; going there and coming back beats losing
              the id already typed into the field above.
            */}
            <Link
              to="/depots"
              className="self-start text-micro font-semibold uppercase tracking-[0.11em] text-signal underline decoration-1 underline-offset-4 hover:no-underline"
            >
              Manage depots
            </Link>
          </div>

          <Field
            label="Driver-facing name"
            value={displayLabel}
            maxLength={DISPLAY_LABEL_MAX}
            onChange={(event) => setDisplayLabel(event.target.value)}
            placeholder="Riverside Mall — Bay 3"
            hint="Optional. What the place is called, as a driver would say it. Left empty, drivers see the station's id."
            {...(nameIssue === undefined ? {} : { error: nameIssue })}
          />

          <Field
            label="Heartbeat interval (s)"
            type="number"
            inputMode="numeric"
            step={1}
            min={HEARTBEAT_MIN_S}
            value={heartbeat}
            onChange={(event) => setHeartbeat(event.target.value)}
            placeholder="300"
            hint="Optional. Left empty, the deployment's default (300s unless the server sets another). Changeable later on the station's page."
            {...(heartbeatIssue === undefined ? {} : { error: heartbeatIssue })}
          />

          <div className="flex items-end md:col-span-2 xl:col-span-3">
            <Button
              type="submit"
              variant="primary"
              disabled={!canSubmit}
              pending={register.isPending}
            >
              Register and show password
            </Button>
          </div>
        </form>
      )}

      <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
        The password is generated here and shown once on the next screen. The station stays{" "}
        <span className="font-mono">Pending</span> until it completes its first BootNotification, so
        a registered station that never appears online is a station that has not connected yet.
      </p>
    </section>
  );
}
